/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { effect, inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpStatusCode } from '@angular/common/http';
import { Store } from '@ngrx/store';
import {
    clearUsersSubscribedToCluster,
    connectionStatus,
    deleteUserAuthenticationRecord,
    initializeUserState,
    initUser,
    newUserAuthenticationRecord,
    remoteLogoutEvent,
    updateUserAuthenticationRecord,
    updateUserMultipleSubscriptions
} from '../+state/user.actions';
import { defer, from, Observable, of, pairwise, retry } from 'rxjs';
import { ConnectionStatus, LogoutRequest, UserAuthenticationRecord } from 'CalltakingCoreApi';
import { catchError, debounceTime, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { KeycloakEventType } from 'keycloak-angular';
import { VestaKeycloakTokenParsed } from '../model/vesta-keycloak-token-parsed';
import { selectLogoffDisabled } from '../../call/+state/call.selectors';
import { ClusterUrlProviderService } from '../../core/services/cluster-url-provider.service';
import { selectConnectionStatusByClusterName, selectMediaConnectionStatus } from '../../call/+state/media.selectors';
import { selectClusterConfigurationMap } from '../../configuration/+state/configuration.selectors';
import { selectUserEffectsInitialized, selectUserStateInitializationTimeMultipleSubscriptions } from '../+state/user.selectors';
import { EnvironmentService } from '../../core/services/environment.service';
import { HeaderLoginService } from '@msi/commandcentral-user-authentication';
import { InterAppCommunicationChannelService } from '@msi/commandcentral-common-header';
import { Topic } from '../model/multiple-subscriptions';
import { StompManagerService } from '../../core/services/stomp-manager/stomp-manager.service';
import { StompBaseConsumer } from '../../core/abstract/stomp-base-consumer';
import { RxStompState } from '@stomp/rx-stomp';
import { KeycloakService } from '../../core/services/keycloak-service';
import { CCAdminProxyService } from '../../core/services/cc-admin-proxy-service';

@Injectable({
    providedIn: 'root'
})
export class UserService extends StompBaseConsumer {
    static readonly USER_AUTHENTICATION_LOAD = '/api/users/v1/load';
    static readonly USER_CONNECTION_STATUS = '/api/users/v1/connection-status';
    static readonly USER_LOGOUT_REQUEST = '/api/users/v1/logout-request';

    private logoffDisabled$: Observable<boolean> | undefined;

    private interAppCommunicationChannelService: InterAppCommunicationChannelService;

    constructor(
        private http: HttpClient,
        private keycloakService: KeycloakService,
        private clusterUrlProviderService: ClusterUrlProviderService,
        private headerLoginService: HeaderLoginService,
        private env: EnvironmentService,
        private ccAdminProxyService: CCAdminProxyService,
        protected stompManagerService: StompManagerService,
        protected store: Store
    ) {
        super(stompManagerService, store);
        if (env.environment.featureFlags?.ccAdminIntegrated) {
            this.interAppCommunicationChannelService = inject(InterAppCommunicationChannelService);
        }

        this.store.select(selectUserEffectsInitialized)
            .pipe(filter((init) => init))
            .subscribe(() => this.updateToken());
        effect(() => {
            const keycloakEvent = this.keycloakService.keycloakSignal();
            if (keycloakEvent.type === KeycloakEventType.AuthRefreshSuccess) {
                console.debug('KeycloakEventType.OnAuthRefreshSuccess event received.');
                this.updateToken();
            }
            if (keycloakEvent.type === KeycloakEventType.AuthLogout) {
                console.warn('KeycloakEventType.OnAuthLogout event received');
                this.logoffDisabled$ = this.store.select(selectLogoffDisabled);
                this.logoffDisabled$.subscribe((logoffDisabled) => {
                    if (!logoffDisabled) {
                        console.warn('KeycloakEventType.OnAuthLogout: page reloading.');
                        window.location.reload();
                    }
                });
            }
            if (keycloakEvent.type === KeycloakEventType.TokenExpired) {
                console.warn('KeycloakEventType.TokenExpired event received. Attempting refresh.');
                defer(() => from(this.keycloakService.keycloak.updateToken(-1)))
                    .pipe(
                        filter((success) => !success),
                        tap(() => console.error('Failed to refresh token')),
                        map(() => {
                            throw Error('Failed to refresh token');
                        }),
                        retry({ count: 3, delay: 5000 }),
                        catchError(() => this.logout())
                    )
                    .subscribe();
            }
        });
        this.multiConnectionInit();
    }

    multiConnectionInit() {
        this.store
            .select(selectMediaConnectionStatus)
            .pipe(
                debounceTime(250),
                filter((val) => !!val)
            )
            .subscribe((mediaConnectionMap) => this.store.dispatch(connectionStatus({ status: mediaConnectionMap })));


        this.store
            .select(selectClusterConfigurationMap)
            .pipe(
                filter((clusterMap) => Boolean(Object.keys(clusterMap).length)),
                take(1)
            )
            .subscribe((clusterMap) =>
                Object.keys(clusterMap).forEach((clusterName) => {
                    this.store.select(selectConnectionStatusByClusterName(clusterName))
                        .pipe(
                            pairwise(),
                            filter(([prev, curr]) => prev === RxStompState.OPEN && curr !== RxStompState.OPEN)
                        )
                        .subscribe(() => this.store.dispatch(clearUsersSubscribedToCluster({ clusterName })));


                    this.store
                        .select(selectUserStateInitializationTimeMultipleSubscriptions(clusterName))
                        .pipe(filter((val) => !!val))
                        .subscribe((init) => this.store.dispatch(initializeUserState({
                            clusterName: clusterName,
                            initializationTime: init
                        })));
                }));
    }

    getTopics(): Topic[] {
        return [Topic.USER_AUTHENTICATION_NEW, Topic.USER_AUTHENTICATION_EDIT, Topic.USER_AUTHENTICATION_DELETE, Topic.USER_LOGOUT, Topic.FORCED_LOGOUT];
    }

    protected getTopicHandlers(): { [topic in Topic]?: (message: any) => void } {
        {
            return {
                [Topic.USER_AUTHENTICATION_NEW]: this.handleAuthNewMessage.bind(this),
                [Topic.USER_AUTHENTICATION_EDIT]: this.handleAuthEditMessage.bind(this),
                [Topic.USER_AUTHENTICATION_DELETE]: this.handleAuthDeleteMessage.bind(this),
                [Topic.USER_LOGOUT]: this.handleAuthLogoutMessage.bind(this),
                [Topic.FORCED_LOGOUT] : this.handleAuthLogoutMessage.bind(this)
            };
        }
    }

    protected getTopicDelays(): { [topic: string]: number } {
        return {
            [Topic.USER_AUTHENTICATION_DELETE]: 1000
        };
    }

    handleAuthNewMessage(userAuthenticationRecord: UserAuthenticationRecord) {
        this.store.dispatch(newUserAuthenticationRecord({ userAuthenticationRecord }));
    }

    handleAuthEditMessage(userAuthenticationRecord: UserAuthenticationRecord) {
        this.store.dispatch(updateUserAuthenticationRecord({ userAuthenticationRecord }));
    }

    handleAuthDeleteMessage(userAuthenticationRecord: UserAuthenticationRecord) {
        this.store.dispatch(deleteUserAuthenticationRecord({ userAuthenticationRecord }));
    }

    handleAuthLogoutMessage(logoutRequest: LogoutRequest) {
        this.store.dispatch(remoteLogoutEvent({ logoutRequest }));
    }


    handleSubscriptionInit(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} User topic ${topic} subscription success: ${receiptId}`);
        this.store.dispatch(updateUserMultipleSubscriptions({ topic: topic as Topic, clusterName, status: true }));
    }

    handleSubscriptionEnd(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} User topic ${topic} subscription end: ${receiptId}`);
        this.store.dispatch(updateUserMultipleSubscriptions({ topic: topic as Topic, clusterName, status: false }));
    }

    handleStompError(topic: string, receiptId: string, error: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} User Authentication Records topic ${topic} subscription failed=${error} for receiptId=${receiptId}`);
    }

    private updateToken() {
        let parsedToken = this.keycloakService.keycloak.tokenParsed;
        this.store.dispatch(initUser({
                token: this.keycloakService.keycloak.token,
                tokenParsed: parsedToken as VestaKeycloakTokenParsed
            }));
    }

    public requestUserAuthenticationRecords(clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, UserService.USER_AUTHENTICATION_LOAD);
        return this.http.get<UserAuthenticationRecord[]>(url, { observe: 'body', responseType: 'json' });
    }

    public reportConnectionStatus(mediaStatus: { [asterisk: string]: ConnectionStatus }, clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${UserService.USER_CONNECTION_STATUS}`);
        return this.http.post(`${url}`, mediaStatus);
    }

    public sendLogoutRequest( clusterName: string, forcedLogout: boolean) {
        let params = new HttpParams();
        params = forcedLogout ? params.append('forced', forcedLogout) : params;
        return this.http.post(this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${UserService.USER_LOGOUT_REQUEST}`), {}, { params: params }).pipe(
            catchError((err: HttpErrorResponse) => {
                if (err.status === HttpStatusCode.NotFound && err.error?.includes?.('UserAuthenticationRecord not found')) {
                    return of(true);
                }
                throw err;
            })
        );
    }

    public logout() {
        // TODO cleanup ccAdminIntegrated flag and simplify it after confirmed that ccadmin will be present both in OP and CL on main branch
        if (this.env.environment.featureFlags?.ccAdminIntegrated && !this.env.environment.IS_CLOUD_ENVIRONMENT) {
            return this.ccAdminProxyService.currentStatus.pipe(
                take(1),
                switchMap((isOnline) =>
                    isOnline
                        ? from(this.headerLoginService.logout().then(() => this.interAppCommunicationChannelService.postMessage({ forceLogout: true })))
                        : from(this.keycloakService.keycloak.logout())
                )
            );
        }
        return this.env.environment.featureFlags?.ccAdminIntegrated
            ? from(this.headerLoginService.logout().then(() => this.interAppCommunicationChannelService.postMessage({ forceLogout: true })))
            : from(this.keycloakService.keycloak.logout());
    }
}
